import type { RuteroBookingTax } from '@bandido/shared/types';

import type { RuteroMoney, TravelBooking } from './queries';

export interface CarBookingPaymentView {
  providerTotal?: RuteroMoney;
  serviceBase?: RuteroMoney;
  pickupCharges: Array<{ name?: string; amount: RuteroMoney }>;
  paymentType?: 'ppd' | 'pod';
}

const money = (amountMinor: unknown, currency: unknown): RuteroMoney | undefined =>
  Number.isInteger(amountMinor) && typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)
    ? { amountMinor: amountMinor as number, currency }
    : undefined;

const pickupCharge = (tax: RuteroBookingTax): CarBookingPaymentView['pickupCharges'][number] | undefined => {
  if (tax.payableAtPickup !== true) return undefined;
  const amount = money(tax.amount?.amountMinor, tax.amount?.currency);
  return amount ? { ...(tax.name ? { name: tax.name } : {}), amount } : undefined;
};

/**
 * Proyección de lectura del desglose contractual de Cars. No suma, resta ni
 * convierte importes: `booking.price` sigue siendo el total autoritativo;
 * `pricing.base` y cada tax conservan su propia moneda.
 */
export function resolveCarBookingPayment(
  booking: Pick<
    TravelBooking,
    | 'vertical'
    | 'quotedAmountMinor'
    | 'currency'
    | 'pricingBaseAmountMinor'
    | 'pricingBaseCurrency'
    | 'quotedTaxes'
    | 'paymentType'
  >,
): CarBookingPaymentView | null {
  if (booking.vertical !== 'cars') return null;
  const providerTotal = money(booking.quotedAmountMinor, booking.currency);
  const serviceBase = money(booking.pricingBaseAmountMinor, booking.pricingBaseCurrency);
  const pickupCharges = Array.isArray(booking.quotedTaxes)
    ? booking.quotedTaxes.flatMap((tax) => {
      const charge = pickupCharge(tax);
      return charge ? [charge] : [];
    })
    : [];
  const paymentType = booking.paymentType === 'ppd' || booking.paymentType === 'pod' ? booking.paymentType : undefined;
  return { providerTotal, serviceBase, pickupCharges, paymentType };
}
